import React, { useEffect, useRef, useState } from 'react';
import 'twin.macro';

import TextEditor from 'components/form/text-editor/TextEditor';
import Container from 'components/container/Container';
import ContainerAction from 'components/container/container-action/ContainerAction';
import SaveStatus from 'components/save-status/SaveStatus';
import UndoRedo from 'components/undo-redo/UndoRedo';
import { useAutoSave } from 'hooks/useAutoSave';
import { useUndoRedo } from 'hooks/useUndoRedo';
import { usePendingSave } from 'context/PendingSaveContext';
import { sendKeepaliveSave } from 'utils/keepaliveSave';
import OutOfDateBox from 'components/out-of-date/OutOfDateBox';
import { useKeptVersion } from 'api/keptVersion';
import {
  noteBody,
  notePath,
  saveCandidateNote,
} from 'api/candidateNotesAutoSave';

interface CandidateNotesProps {
  procedureId: number;
  defaultValue?: string;
  /** The note the server now holds, so that coming back to this tab shows it. */
  onStored?: (notes: string) => void;
}

/**
 * A candidate's note stores itself (auto-save, D4): no Save button; the
 * note is stored a moment after the person stops typing, and the corner
 * says so. It never waits for a publish.
 */
const CandidateNotes: React.FC<CandidateNotesProps> = ({
  procedureId,
  defaultValue = '',
  onStored,
}) => {
  const [notes, setNotes] = useState<string>(defaultValue ?? '');
  /** Raised to draw the text box again from `notes` (after Undo or Redo). */
  const [drawn, setDrawn] = useState(0);
  const drawnNow = useRef(drawn);
  drawnNow.current = drawn;
  const { setPending, registerAbandon, registerSaveNow, switching } =
    usePendingSave();

  const version = useKeptVersion('note', procedureId);

  const {
    state: saveState,
    keptAt,
    hasPending,
    hasFailed,
    abandon,
    saveNow,
    isKept,
    refusal,
    retryRefused,
  } = useAutoSave<string>({
    value: notes,
    identity: `candidate-note:${procedureId}`,
    save: async (value, signal) => {
      version.stored(
        procedureId,
        await saveCandidateNote(
          procedureId,
          value,
          signal,
          version.basedOn(procedureId)
        )
      );
      onStored?.(value);
    },
    flushOnHide: (value) => {
      // With the count: refused when someone else changed the note.
      sendKeepaliveSave(
        notePath(procedureId),
        noteBody(value),
        'PUT',
        version.basedOn(procedureId)
      );
      // Also on this road the screen around it must know the newer note:
      // coming back to this tab showed the older one, and the next typing
      // stored over the words just sent (review of the slice).
      onStored?.(value);
    },
  });

  const { canUndo, canRedo, undo, redo } = useUndoRedo<string>({
    value: notes,
    identity: `candidate-note:${procedureId}`,
    apply: (earlier) => {
      setNotes(earlier);
      // The text box only starts from its value: drawn again.
      setDrawn((n) => n + 1);
    },
  });

  // What the sidebar asks before taking the person away.
  useEffect(() => {
    setPending(hasPending && hasFailed);
    registerAbandon(abandon);
    registerSaveNow(saveNow, isKept);
    return () => {
      setPending(false);
      registerAbandon(null);
      registerSaveNow(null);
    };
  }, [
    hasPending,
    hasFailed,
    setPending,
    registerAbandon,
    abandon,
    registerSaveNow,
    saveNow,
    isKept,
  ]);

  return (
    <Container>
      <div data-testid="candidate-notes">
        <TextEditor
          key={drawn}
          identifier="candidate_notes"
          defaultValue={notes}
          // Only the text box that is on screen speaks. The one that Undo
          // or Redo has just replaced still reports its words as it goes,
          // and they came back as the note while the screen showed the
          // undone text (seen in the browser, 5 Oct 2026).
          onChange={(value) => {
            if (drawnNow.current === drawn) setNotes(value);
          }}
        />
      </div>

      <OutOfDateBox
        refusal={refusal}
        onLoadTheirs={() => {
          abandon();
          window.setTimeout(() => window.location.reload(), 100);
        }}
        onKeepMine={() => {
          version.keepMine();
          retryRefused();
        }}
      />

      <ContainerAction>
        <div tw="flex items-center gap-2">
          <UndoRedo
            canUndo={canUndo}
            canRedo={canRedo}
            onUndo={undo}
            onRedo={redo}
          />
          <SaveStatus state={saveState} keptAt={keptAt} switching={switching} />
        </div>
      </ContainerAction>
    </Container>
  );
};

export default CandidateNotes;
